import { useState, type FormEvent } from "react";
import { Activity, Loader2, Moon, Sun } from "lucide-react";
import { ApiError } from "@/lib/api";
import { useTheme } from "@/app/theme-provider";
import { PublicTicketForm } from "@/features/public/PublicTicketForm";
import { PublicStatusForm } from "@/features/public/PublicStatusForm";
import { ForgotPasswordForm } from "./ForgotPasswordForm";
import { SignupForm } from "./SignupForm";
import { useAuth } from "./auth-context";
import { useBranding } from "@/features/branding/hooks";
import { COMPANY_LOGO_URL } from "@/features/branding/api";

// A ticket number in the URL (?ticket=3HS-…, from an acknowledgement email)
// opens the status checker prefilled.
const initialTicket =
  typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("ticket") ?? ""
    : "";

// A reset token in the URL (?reset_token=…, from the reset email) opens the
// "set a new password" form directly.
const initialResetToken =
  typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("reset_token") ?? ""
    : "";

export default function LoginPage() {
  const { login } = useAuth();
  const { resolved, setTheme } = useTheme();
  const { data: branding } = useBranding();
  const [mode, setMode] = useState<
    "login" | "signup" | "public" | "check" | "forgot"
  >(initialResetToken ? "forgot" : initialTicket ? "check" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({ email, password });
    } catch (err) {
      // Surface the server's message verbatim. It is deliberately identical
      // for "unknown user" and "wrong password" so the form cannot be used
      // to enumerate accounts — rewording it here would undo that.
      setError(
        err instanceof ApiError ? err.message : "Something went wrong. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <button
        type="button"
        onClick={() => setTheme(resolved === "dark" ? "light" : "dark")}
        aria-label={`Switch to ${resolved === "dark" ? "light" : "dark"} theme`}
        className="absolute right-4 top-4 rounded-md border border-border p-2 hover:bg-accent"
      >
        {resolved === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </button>

      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center text-center">
          {branding?.has_logo ? (
            <img
              src={COMPANY_LOGO_URL}
              alt=""
              className="h-14 w-auto max-w-[220px] object-contain"
            />
          ) : (
            <Activity className="h-8 w-8 text-primary" aria-hidden="true" />
          )}
          <h1 className="mt-3 text-xl font-semibold tracking-tight">
            {branding?.name?.trim() || "3Handshake HelpDesk"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {mode === "login"
              ? "Sign in to continue"
              : mode === "signup"
                ? "Create your account"
                : mode === "public"
                  ? "Raise a request"
                  : mode === "check"
                    ? "Check ticket status"
                    : "Reset your password"}
          </p>
        </div>

        {mode === "signup" ? (
          <SignupForm
            onBack={() => setMode("login")}
            onRegistered={(e) => setEmail(e)}
          />
        ) : mode === "public" ? (
          <PublicTicketForm onBack={() => setMode("login")} />
        ) : mode === "check" ? (
          <PublicStatusForm
            onBack={() => setMode("login")}
            initialTicketNumber={initialTicket}
          />
        ) : mode === "forgot" ? (
          <ForgotPasswordForm
            onBack={() => setMode("login")}
            resetToken={initialResetToken || undefined}
          />
        ) : (
        <form
          onSubmit={handleSubmit}
          className="space-y-4 rounded-lg border border-border bg-card p-6"
          noValidate
        >
          {error && (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </div>
          )}

          <div className="space-y-1.5">
            <label htmlFor="email" className="text-sm font-medium">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="password" className="text-sm font-medium">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          <button
            type="submit"
            disabled={submitting || !email || !password}
            className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-primary text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
        )}

        {mode === "login" && (
          <div className="mt-4 space-y-1.5 text-center text-sm text-muted-foreground">
            <p>
              <button
                type="button"
                onClick={() => setMode("forgot")}
                className="font-medium text-primary hover:underline"
              >
                Forgot password?
              </button>
            </p>
            <p>
              New customer?{" "}
              <button
                type="button"
                onClick={() => setMode("signup")}
                className="font-medium text-primary hover:underline"
              >
                Create an account
              </button>
            </p>
            <p>
              Or{" "}
              <button
                type="button"
                onClick={() => setMode("public")}
                className="font-medium text-primary hover:underline"
              >
                raise a request without signing in
              </button>
            </p>
            <p>
              Already have a ticket?{" "}
              <button
                type="button"
                onClick={() => setMode("check")}
                className="font-medium text-primary hover:underline"
              >
                Check ticket status
              </button>
            </p>
          </div>
        )}

        <p className="mt-6 text-center text-xs text-muted-foreground">
          3Handshake TechSoft Pvt Ltd
        </p>
      </div>
    </div>
  );
}
